/**
 * Connection modal for creating/editing database connections.
 * Inspired by DBeaver's Connection Settings dialog.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Connection } from '../types';
import { connectionApi } from '../services/api';
import SvgIcon from './SvgIcon';

interface ConnectionModalProps {
  open: boolean;
  connection: Connection | null;
  onClose: () => void;
  onSave: (connection: Connection) => void;
}

const ConnectionModal: React.FC<ConnectionModalProps> = ({
  open,
  connection,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Connection>({
    id: '',
    name: '',
    type: 'mysql',
    host: 'localhost',
    port: 3306,
    database: '',
    username: '',
    password: '',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (connection) {
      setFormData(connection);
    } else {
      // Reset form for new connection
      setFormData({
        id: '',
        name: '',
        type: 'mysql',
        host: 'localhost',
        port: 3306,
        database: '',
        username: '',
        password: '',
      });
    }
    setTestResult(null);
  }, [connection, open]);

  const handleChange = (field: keyof Connection, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Update default port when type changes
      if (field === 'type') {
        updated.port = value === 'mysql' ? 3306 : 5432;
      }
      
      return updated;
    });
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await connectionApi.test(formData);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.message || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      let saved: Connection;
      if (formData.id) {
        saved = await connectionApi.update(formData.id, formData);
        // Restore password from formData since backend hides it
        saved.password = formData.password;
      } else {
        saved = await connectionApi.create(formData);
        // Restore password from formData since backend hides it
        saved.password = formData.password;
      }
      onSave(saved);
      onClose();
    } catch (err) {
      console.error('Failed to save connection:', err);
    }
  };

  const isValid = formData.name && formData.host && formData.database && formData.username;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {formData.type === 'mysql' || formData.type === 'postgresql' ? (
            <SvgIcon iconName={formData.type} size={24} />
          ) : (
            <SvgIcon iconName="mysql" size={24} />
          )}
          {connection ? 'Edit Connection' : 'New Connection'}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Connection Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Database Type"
            select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            fullWidth
            required
          >
            <MenuItem value="mysql">MySQL</MenuItem>
            <MenuItem value="postgresql">PostgreSQL</MenuItem>
          </TextField>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Host"
              value={formData.host}
              onChange={(e) => handleChange('host', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Port"
              type="number"
              value={formData.port}
              onChange={(e) => handleChange('port', parseInt(e.target.value))}
              sx={{ width: '150px' }}
              required
            />
          </Box>

          <TextField
            label="Database"
            value={formData.database}
            onChange={(e) => handleChange('database', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Username"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            fullWidth
          />

          {testResult && (
            <Alert severity={testResult.success ? 'success' : 'error'}>
              {testResult.message}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleTestConnection}
          disabled={!isValid || testing}
          startIcon={testing && <CircularProgress size={16} />}
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionModal;

